import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { accessKeyErrorMessage } from "@/hooks/use-access-keys";
import {
  useApplyDeskImprovements,
  useSetAdviceStatus,
} from "@/hooks/use-desk-improvements";
import { formatDateTime } from "@/lib/date-utils";
import type {
  AdviceStatusRequest,
  DeskImprovementRun,
  DeskImprovementSuggestion,
} from "@/lib/types/desk-improvements";

const ADVICE_LABELS: Record<string, string> = {
  catalog_item_team: "Catalog item team",
  membership: "Membership",
  disable_desk: "Disable desk",
  routing_method: "Routing method",
  merge: "Merge",
  split: "Split",
  other: "Other",
};

function adviceLabel(category: string | null): string {
  if (!category) return "Other";
  // eslint-disable-next-line security/detect-object-injection
  return ADVICE_LABELS[category] ?? category;
}

function EvidenceIds({ ids }: { ids: string[] }) {
  if (ids.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      <span className="text-muted-foreground">Evidence:</span>
      {ids.map((id) => (
        <code key={id} className="bg-muted rounded px-1 py-0.5 font-mono">
          {id}
        </code>
      ))}
    </div>
  );
}

function StatusBadge({
  suggestion,
}: {
  suggestion: DeskImprovementSuggestion;
}) {
  if (suggestion.status === "applied")
    return <Badge variant="positive">Applied</Badge>;
  if (suggestion.status === "accepted")
    return <Badge variant="warning">Not applied</Badge>;
  return null;
}

function ChangeCard({
  suggestion,
  checked,
  disabled,
  onCheckedChange,
}: {
  suggestion: DeskImprovementSuggestion;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (value: boolean) => void;
}) {
  const applied = suggestion.status === "applied";
  const isRouting = suggestion.kind === "routing";
  return (
    <div
      data-testid={`suggestion-${suggestion.id}`}
      className="space-y-3 rounded-lg border p-4"
    >
      <div className="flex items-start gap-3">
        <Checkbox
          aria-label={`Select ${suggestion.target_name} ${suggestion.kind}`}
          checked={checked}
          disabled={disabled || applied}
          onCheckedChange={(value) => {
            onCheckedChange(value === true);
          }}
          className="mt-0.5"
        />
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <span className="font-medium">{suggestion.target_name}</span>
          <Badge variant="outline">
            {isRouting ? "Routing method" : "Description"}
          </Badge>
          <StatusBadge suggestion={suggestion} />
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium uppercase">
            Current
          </p>
          <p className="bg-muted/50 whitespace-pre-wrap break-words rounded p-2 text-sm">
            {suggestion.current_text || (
              <span className="text-muted-foreground italic">
                No description
              </span>
            )}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium uppercase">
            Proposed
          </p>
          <p className="whitespace-pre-wrap break-words rounded bg-emerald-500/10 p-2 text-sm">
            {suggestion.proposed_text}
          </p>
        </div>
      </div>
      {suggestion.rationale && (
        <p className="text-sm">
          <span className="text-muted-foreground">Why: </span>
          {suggestion.rationale}
        </p>
      )}
      <EvidenceIds ids={suggestion.evidence_ticket_ids} />
      {suggestion.apply_error && suggestion.status !== "applied" && (
        <p className="text-destructive text-sm">{suggestion.apply_error}</p>
      )}
    </div>
  );
}

function AdviceRow({
  suggestion,
  pending,
  onStatus,
}: {
  suggestion: DeskImprovementSuggestion;
  pending: boolean;
  onStatus: (status: AdviceStatusRequest) => void;
}) {
  const done = suggestion.status === "applied";
  const rejected = suggestion.status === "rejected";
  return (
    <li
      data-testid={`advice-${suggestion.id}`}
      className="flex flex-col gap-2 rounded-lg border p-4 md:flex-row md:items-start"
    >
      <div className="flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{adviceLabel(suggestion.category)}</Badge>
          <span className="font-medium">{suggestion.target_name}</span>
          {done && <Badge variant="positive">Done</Badge>}
          {rejected && <Badge variant="destructive">Rejected</Badge>}
        </div>
        <p
          className={
            rejected
              ? "text-muted-foreground break-words text-sm line-through"
              : "break-words text-sm"
          }
        >
          {suggestion.proposed_text}
        </p>
        {suggestion.rationale && (
          <p className="text-muted-foreground text-sm">
            {suggestion.rationale}
          </p>
        )}
        <EvidenceIds ids={suggestion.evidence_ticket_ids} />
      </div>
      <div className="flex gap-2">
        {/* one button that toggles, so keyboard focus stays on it */}
        <Button
          size="sm"
          variant={done || rejected ? "outline" : "default"}
          disabled={pending}
          onClick={() => {
            onStatus(done || rejected ? "pending" : "done");
          }}
        >
          {done || rejected ? "Undo" : "Mark done"}
        </Button>
        {!done && !rejected && (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => {
              onStatus("rejected");
            }}
          >
            Reject
          </Button>
        )}
      </div>
    </li>
  );
}

interface ImprovementReviewProps {
  tenantId: string;
  run: DeskImprovementRun;
}

export function ImprovementReview({ tenantId, run }: ImprovementReviewProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const apply = useApplyDeskImprovements();
  const setAdviceStatus = useSetAdviceStatus();

  const changes = run.suggestions.filter((s) => s.kind !== "structural");
  const advice = run.suggestions.filter((s) => s.kind === "structural");
  const selectable = changes.filter((s) => s.status !== "applied");
  const chosen = selectable.filter((s) => selected.has(s.id));

  const toggle = (id: string, value: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (value) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleApply = async () => {
    let result;
    try {
      result = await apply.mutateAsync({
        tenantId,
        runId: run.id,
        suggestionIds: chosen.map((s) => s.id),
      });
    } catch (error: unknown) {
      toast.error(accessKeyErrorMessage(error, "Applying the changes failed"));
      return;
    }
    const count = (outcome: string) =>
      result.outcomes.filter((o) => o.outcome === outcome).length;
    const applied = count("applied") + count("already_applied");
    const notApplied = count("stale") + count("failed");
    setSelected(new Set());
    const message =
      `${applied} applied` +
      (notApplied ? `, ${notApplied} not applied (see the cards)` : "");
    if (notApplied) toast.warning(message);
    else toast.success(message);
  };

  const handleAdvice = async (
    suggestion: DeskImprovementSuggestion,
    status: AdviceStatusRequest,
  ) => {
    try {
      await setAdviceStatus.mutateAsync({
        tenantId,
        runId: run.id,
        suggestionId: suggestion.id,
        status,
      });
    } catch (error: unknown) {
      toast.error(accessKeyErrorMessage(error, "Updating the advice failed"));
    }
  };

  return (
    <div className="space-y-6">
      <dl
        data-testid="run-stats"
        className="text-muted-foreground grid grid-cols-2 gap-2 text-sm md:grid-cols-4"
      >
        <div>
          <dt>Tickets read</dt>
          <dd className="text-foreground font-medium">
            {run.ticket_count?.toLocaleString() ?? "—"}
          </dd>
        </div>
        <div>
          <dt>Hand moves</dt>
          <dd className="text-foreground font-medium">
            {run.move_count?.toLocaleString() ?? "—"}
          </dd>
        </div>
        <div>
          <dt>Corrected tickets</dt>
          <dd className="text-foreground font-medium">
            {run.corrected_ticket_count?.toLocaleString() ?? "—"}
          </dd>
        </div>
        <div>
          <dt>Implied corrections</dt>
          <dd className="text-foreground font-medium">
            {run.implied_correction_count?.toLocaleString() ?? "—"}
          </dd>
        </div>
      </dl>
      {run.window_start && run.window_end && (
        <p className="text-muted-foreground text-sm">
          Tickets created {formatDateTime(run.window_start)} to{" "}
          {formatDateTime(run.window_end)}
          {run.events_from &&
            run.events_from > run.window_start &&
            ` · moves recorded from ${formatDateTime(run.events_from)}`}
        </p>
      )}
      {run.notes && run.notes.length > 0 && (
        <div className="text-muted-foreground text-sm">
          <p>Notes from the run:</p>
          <ul className="list-disc pl-5">
            {run.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            Description and routing changes
          </h2>
          {selectable.length > 0 && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={apply.isPending}
                onClick={() => {
                  setSelected(new Set(selectable.map((s) => s.id)));
                }}
              >
                Select all
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={apply.isPending}
                onClick={() => {
                  setSelected(new Set());
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          Review each proposal before applying it: the texts are a starting
          point, not a verdict. Applying writes the proposed text to the
          tenant&apos;s desk; a desk edited since the run is left as it is.
        </p>
        {changes.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            The run proposed no description or routing change.
          </p>
        ) : (
          <div className="space-y-3">
            {changes.map((s) => (
              <ChangeCard
                key={s.id}
                suggestion={s}
                checked={selected.has(s.id)}
                disabled={apply.isPending}
                onCheckedChange={(value) => {
                  toggle(s.id, value);
                }}
              />
            ))}
          </div>
        )}
        {changes.length > 0 && (
          <div className="flex justify-end">
            <Button
              data-testid="apply-selected"
              disabled={chosen.length === 0 || apply.isPending}
              onClick={() => {
                setConfirmOpen(true);
              }}
            >
              {apply.isPending && (
                <Loader2 aria-hidden className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              {chosen.length > 0
                ? `Apply selected (${chosen.length})`
                : "Nothing selected"}
            </Button>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Configuration advice</h2>
        <p className="text-muted-foreground text-sm">
          Settings the run cannot change for you. Make each change by hand in
          the tenant, then mark it done.
        </p>
        {advice.length === 0 ? (
          <p className="text-muted-foreground text-sm">No advice.</p>
        ) : (
          <ul className="space-y-3">
            {advice.map((s) => (
              <AdviceRow
                key={s.id}
                suggestion={s}
                pending={
                  setAdviceStatus.isPending &&
                  setAdviceStatus.variables.suggestionId === s.id
                }
                onStatus={(status) => {
                  void handleAdvice(s, status);
                }}
              />
            ))}
          </ul>
        )}
      </section>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply {chosen.length} change{chosen.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The proposed texts replace the tenant&apos;s current desk and team
              settings right away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="text-muted-foreground max-h-[50vh] list-disc space-y-1 overflow-y-auto pl-5 text-sm">
            {chosen.map((s) => (
              <li key={s.id}>
                {s.target_name}:{" "}
                {s.kind === "routing"
                  ? `routing ${s.current_text ?? "?"} → ${s.proposed_text}`
                  : "description"}
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleApply();
              }}
            >
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
